import {
  CallParticipant,
  CallParticipantPermitsUpdate,
  CallParticipantStatesUpdate,
  CallParticipantStatusUpdate,
  ParticipantRoles,
  ParticipantRolesEnum,
  ParticipantState,
  SetDevicesInitialValuesProps,
  SetInitDevicesValueProps,
  UpdateParticipantStateProps,
  UpdateParticipantStatusProps,
} from '@nauTypes/callType';

const adminRoles: ParticipantRoles[] = [ParticipantRolesEnum.organizer, ParticipantRolesEnum.admin];
export const isAdminRole = (role: ParticipantRoles): boolean => adminRoles.includes(role);

const updateParticipantStatus = ({ participant, updates }: UpdateParticipantStatusProps): CallParticipant =>
  updates.users.includes(participant.user) ? { ...participant, status: updates.status } : participant;

export const updateParticipantsStatus = (
  participants: CallParticipant[],
  updates: CallParticipantStatusUpdate
): CallParticipant[] => participants.map((participant) => updateParticipantStatus({ participant, updates }));

type Permits = CallParticipant['permits'];

const addPermits = (permits: Permits, newPermits: Permits) => {
  return [...permits, ...newPermits];
};

const deletePermits = (permits: Permits, permitsToRemove: Permits) => {
  return permits.filter((permit) => !permitsToRemove.includes(permit));
};

const updatePermits = ({
  participant,
  changes,
}: {
  participant: CallParticipant;
  changes: CallParticipantPermitsUpdate;
}): CallParticipant => {
  let updatedPermits = participant.permits;
  updatedPermits = addPermits(updatedPermits, changes?.addPermits || []);
  updatedPermits = deletePermits(updatedPermits, changes?.delPermits || []);
  return { ...participant, permits: updatedPermits };
};

export const updateParticipantsPermits = ({
  participants,
  changes,
}: {
  participants: CallParticipant[];
  changes: CallParticipantPermitsUpdate;
}): CallParticipant[] => {
  return participants.map((participant) => {
    return participant.user === changes.user ? updatePermits({ changes, participant }) : participant;
  });
};

const updateState = ({
  participantStates,
  newState,
}: {
  participantStates: CallParticipant['states'];
  newState: CallParticipantStatesUpdate;
}) => {
  return participantStates?.map((participantState) =>
    participantState.streamName === newState.streamName
      ? { ...participantState, state: { ...participantState.state, ...newState.state } }
      : participantState
  );
};

export const updateParticipantsState = ({
  participants,
  updates,
}: {
  participants: CallParticipant[];
  updates: CallParticipantStatesUpdate;
}): CallParticipant[] => {
  return participants.map((participant) => {
    return participant.user === updates.user
      ? {
          ...participant,
          states: updateState({
            newState: updates,
            participantStates: participant?.states?.length
              ? participant.states
              : [{ call: 0, id: 0, state: updates.state, streamName: updates.streamName, user: updates.user }],
          }),
        }
      : participant;
  });
};

const setInitDevicesValue = ({ participantState, roomParticipants }: SetInitDevicesValueProps): ParticipantState => {
  return roomParticipants.some((roomParticipant) =>
    Object.hasOwn(roomParticipant.participant?.streams, participantState.streamName)
  )
    ? { ...participantState, state: { ...participantState.state, camOn: true, micOn: true } }
    : participantState;
};

const updateParticipantState = ({ participantStates, roomParticipants }: UpdateParticipantStateProps) => {
  return participantStates?.map((participantState) => setInitDevicesValue({ participantState, roomParticipants }));
};

export const setDevicesInitialValues = ({
  callParticipants,
  roomParticipants,
}: SetDevicesInitialValuesProps): CallParticipant[] => {
  return callParticipants.map((participant) => {
    return participant?.states?.length
      ? { ...participant, states: updateParticipantState({ participantStates: participant.states, roomParticipants }) }
      : participant;
  });
};
